import React from 'react';
import Link from 'next/link';

const PHONE = '9492935721';
const SMS_LINK = `sms:${PHONE}`;
const CALL_LINK = `tel:+1${PHONE}`;

const services = [
  {
    number: '01',
    title: 'DA1MAMBA Full Detail',
    badge: 'Most Popular',
    description: 'The complete reset — interior shampoo & steam, clay bar decontamination, and gloss protection applied with brand-new microfiber towels on every single vehicle.',
    ideal: 'Luxury vehicles, daily drivers, and post-trip resets',
    outcome: 'Showroom-quality results, inside and out',
    accent: 'var(--mamba-purple-bright)',
  },
  {
    number: '02',
    title: 'Paint Correction',
    badge: 'Specialist Service',
    description: 'Dealership-grade correction that safely removes swirls, scratches, haze, and oxidation. We measure defect reduction with paint depth gauges — no guessing, no shortcuts.',
    ideal: 'Trusted on Ferrari, Lamborghini, Porsche, McLaren, and Tesla',
    outcome: 'Up to 90% defect removal — restored clarity and depth',
    accent: 'var(--mamba-gold-light)',
  },
  {
    number: '03',
    title: 'Ceramic Coating',
    badge: 'Long-Term Protection',
    description: 'A long-term hydrophobic barrier that enhances gloss, cuts maintenance time, blocks UV damage, and keeps your paint cleaner between washes — for years, not weeks.',
    ideal: 'Luxury, exotic, and high-value daily drivers',
    outcome: 'Entry-level 6-month to premium 2+ year protection options',
    accent: 'var(--mamba-purple-bright)',
  },
  {
    number: '04',
    title: 'Interior Detailing',
    badge: 'White-Glove Care',
    description: 'Precision vacuum, shampoo & steam, leather clean & condition, panels, vents, and buttons — every surface treated with the right chemistry for a crisp, hygienic cabin.',
    ideal: 'Kids, pets, beach days, daily commuting, or sale prep',
    outcome: 'Fresh, sanitized, showroom-ready interior',
    accent: 'var(--mamba-gold-light)',
  },
  {
    number: '05',
    title: 'Exterior Detailing',
    badge: 'Paint-Safe Finish',
    description: 'Touchless pre-rinse, premium foam bath, clay bar decontamination, gentle drying, trim dressing, and a premium sealant or ceramic topper — all paint-safe, all the time.',
    ideal: 'Quarterly decontamination and pre-sale refresh',
    outcome: 'Crisp, hydrophobic finish with zero swirl risk',
    accent: 'var(--mamba-purple-bright)',
  },
  {
    number: '06',
    title: 'Boat Detailing',
    badge: 'Marine Service',
    description: 'Professional marine vessel detailing — hull cleaning, deck washing, interior cabin detailing, upholstery protection, and chrome polishing for a showroom-ready finish.',
    ideal: 'Boats, yachts, and watercraft across Orange County',
    outcome: 'Showroom-ready marine finish, inside and out',
    accent: 'var(--mamba-gold-light)',
  },
];

export default function ServicesOverview() {
  return (
    <section style={{ padding: '120px 0', background: 'var(--mamba-black)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-16">
          <div className="label-gold mb-4 reveal">
            <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
            Exclusive Services
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2
              className="section-heading reveal reveal-delay-1"
              style={{ fontSize: 'clamp(32px, 4vw, 52px)', maxWidth: '520px' }}
            >
              Premium detailing,<br />
              <span className="text-gradient-purple">precisely executed.</span>
            </h2>
            <p className="reveal reveal-delay-2" style={{ color: 'var(--gray-dim)', fontSize: '15px', lineHeight: '1.75', maxWidth: '340px', fontFamily: 'DM Sans, sans-serif' }}>
              Every package is built for owners who expect more than average — and delivered at your door.
            </p>
          </div>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: 'rgba(107,33,168,0.12)', border: '1px solid rgba(107,33,168,0.12)' }}>
          {services?.map((s, i) => (
            <div
              key={i}
              className="service-card reveal"
              style={{
                transitionDelay: `${i * 0.08}s`,
                background: i === 0 ? 'var(--mamba-graphite-mid)' : 'var(--mamba-graphite)',
                border: 'none',
                padding: '40px 36px',
              }}
            >
              <div className="flex items-start justify-between mb-5">
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '11px', color: s?.accent, letterSpacing: '0.1em' }}>
                  {s?.number}
                </span>
                {s?.badge && (
                  <span style={{
                    background: 'rgba(107,33,168,0.15)',
                    border: '1px solid rgba(107,33,168,0.3)',
                    color: 'var(--mamba-purple-bright)',
                    fontSize: '10px',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '4px 10px',
                  }}>
                    {s?.badge}
                  </span>
                )}
              </div>
              <h3 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--white)', marginBottom: '12px', letterSpacing: '-0.01em' }}>
                {s?.title}
              </h3>
              <p style={{ color: 'var(--gray-dim)', fontSize: '14px', lineHeight: '1.7', fontFamily: 'DM Sans, sans-serif', marginBottom: '20px' }}>
                {s?.description}
              </p>
              <div className="flex flex-col gap-2 mb-6">
                <div className="flex items-start gap-2">
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-gold-light)', flexShrink: 0, marginTop: '6px' }} />
                  <span style={{ color: 'var(--cool-gray)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' }}>{s?.ideal}</span>
                </div>
                <div className="flex items-start gap-2">
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-purple-bright)', flexShrink: 0, marginTop: '6px' }} />
                  <span style={{ color: 'var(--cool-gray)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' }}>{s?.outcome}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <a href={SMS_LINK} className="btn-primary btn-sm">
                  Text Quote
                </a>
                <a href={CALL_LINK} className="btn-ghost btn-sm">
                  Call Now
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* View all CTA */}
        <div className="flex justify-center mt-12 reveal">
          <Link href="/services" className="btn-ghost">
            View All Services & Packages →
          </Link>
        </div>
      </div>
    </section>
  );
}
